import { css } from '@emotion/react';
import { labelStyles } from '@zapier/style-encapsulation';
import { Animation, Colors, ColorsCSS, Shadows } from '../../../theme';
import { createUniqueCustomProperties } from '../../../utils/createUniqueCustomProperty';
import { TypographyCSSFont } from '@/theme/Typography';

const Vars = createUniqueCustomProperties('Button', [
  'baseBackgroundColor',
  'baseBorderColor',
  'baseColor',
  'disabledBackgroundColor',
  'disabledBorderColor',
  'disabledColor',
  'disabledOutlineColor',
  'hoverBackgroundColor',
  'hoverBorderColor',
  'hoverColor',
  'hoverOutlineColor',
  'iconMargin',
  'focusBackgroundColor',
  'focusBorderColor',
  'focusColor',
  'focusOutlineColor',
  'selectedBackgroundColor',
  'selectedBorderColor',
  'selectedColor',
  'selectedOutlineColor',
  'buttonContentPadding',
]);

export const Styles = labelStyles('BaseButton', {
  root: ({ iconSize }: { iconSize: number }) => css`
    // All of the CSS Custom Properties that are used by this
    // component, ensured to be unique. Most are color related
    // and are set below via '[data-color]' selectors.
    ${Vars.baseBackgroundColor}: transparent;
    ${Vars.baseBorderColor}: transparent;
    ${Vars.baseColor}: #000;
    ${Vars.disabledBackgroundColor}: var(--zds-background-strongest, ${Colors.neutral200});
    ${Vars.disabledBorderColor}: var(--zds-background-strongest, ${Colors.neutral400});
    ${Vars.disabledColor}: var(--zds-text-weakest, ${Colors.neutral500});
    ${Vars.disabledOutlineColor}: transparent;
    ${Vars.hoverBackgroundColor}: var(${Vars.baseBackgroundColor});
    ${Vars.hoverBorderColor}: var(${Vars.baseBorderColor});
    ${Vars.hoverColor}: var(${Vars.baseColor});
    ${Vars.hoverOutlineColor}: transparent;
    ${Vars.iconMargin}: 6px;
    ${Vars.focusBackgroundColor}: var(${Vars.baseBackgroundColor});
    ${Vars.focusBorderColor}: transparent;
    ${Vars.focusColor}: var(${Vars.baseColor});
    ${Vars.focusOutlineColor}: var(${Vars.focusBackgroundColor});
    ${Vars.selectedBackgroundColor}: var(${Vars.baseBackgroundColor});
    ${Vars.selectedBorderColor}: transparent;
    ${Vars.selectedColor}: var(${Vars.baseColor});
    ${Vars.selectedOutlineColor}: transparent;
    ${Vars.buttonContentPadding}: 0 10px;

    background-color: var(${Vars.baseBackgroundColor});
    border-color: var(${Vars.baseBorderColor});
    color: var(${Vars.baseColor});

    align-items: center;
    appearance: none;
    border-radius: 3px;
    border-style: solid;
    border-width: 1px;
    box-sizing: border-box;
    position: relative;
    cursor: pointer;
    display: inline-flex;
    justify-content: center;
    // Safari applies 2px margin to buttons this clears it
    margin: 0;
    min-width: 0;
    opacity: 1;
    text-align: center;
    text-decoration: none;
    text-transform: none;
    transition: all 200ms ease-in-out;
    vertical-align: middle;
    white-space: nowrap;

    &::-moz-focus-inner {
      padding: 0;
      border: 0;
    }

    // This is the outline that renders around the button when
    // its focused. Its a pseudo element to preserve transparency;
    // between the edge of the button and the edge of the outline.
    // Its defined here vs only in ':focus' so that it can transition
    // between states.
    &::before {
      content: '';
      position: absolute;
      pointer-events: none;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      border: 2px solid transparent;
      border-radius: 5px;
      transition-property: all;
      transition-timing-function: ${Animation.transitionTimingFunction};
      transition-duration: 200ms;
    }

    &:hover:not([disabled]),
    a:hover & {
      background-color: var(${Vars.hoverBackgroundColor});
      border-color: var(${Vars.hoverBorderColor});
      /* TODO: ask Christophe to create a variable with a 'none' value */
      box-shadow: var(--zds-radius-none, ${Shadows.elevation6});
      color: var(${Vars.hoverColor});
    }

    &:focus-visible:not([disabled]),
    a:focus-visible & {
      background-color: var(${Vars.focusBackgroundColor});
      border-color: var(${Vars.focusBorderColor});
      color: var(${Vars.focusColor});
      outline: none;
      &::before {
        border-color: var(${Vars.focusOutlineColor});
        top: -4px;
        right: -4px;
        bottom: -4px;
        left: -4px;
      }
    }

    &[data-selected]:not([data-color='button-nav']) {
      background-color: var(${Vars.selectedBackgroundColor});
      border-color: var(${Vars.selectedBorderColor});
      color: var(${Vars.selectedColor});
      // Hide :focus outline on selected buttons
      &::before {
        content: none;
      }
    }

    &[data-color='primary'] {
      ${Vars.baseBackgroundColor}: var(--zds-ui-primary-stronger, ${Colors.blue});
      ${Vars.baseColor}: var(--zds-background-weaker, ${Colors.neutral100});
      ${Vars.hoverBackgroundColor}: var(--zds-ui-primary-strongest, ${Colors.night});

      &[data-size='compact'] {
        font: var(--zds-minimal-print-3, ${TypographyCSSFont.paragraph3Semibold});
      }
      &[data-size='xsmall'] {
        font: var(--zds-body-3, ${TypographyCSSFont.paragraph3Semibold});
      }
      &[data-size='small'] {
        font: var(--zds-body-3, ${TypographyCSSFont.paragraph3Semibold});
      }
      &[data-size='medium'] {
        font: var(--zds-body-3, ${TypographyCSSFont.paragraph3Semibold});
      }
      &[data-size='large'] {
        font: var(--zds-paragraph-header-3, ${TypographyCSSFont.paragraph1ShortSemibold});
      }
    }

    &[data-color='primary-alt'] {
      ${Vars.baseBackgroundColor}: var(--zds-gray-warm-10, ${Colors.neutral800});
      ${Vars.baseColor}: var(--zds-gray-warm-1, ${Colors.neutral100});
      ${Vars.hoverBackgroundColor}: var(--zds-gray-warm-9, ${Colors.neutral700});

      &[data-size='compact'] {
        font: var(--zds-minimal-print-3, ${TypographyCSSFont.paragraph3Semibold});
      }
      &[data-size='xsmall'] {
        font: var(--zds-body-3, ${TypographyCSSFont.paragraph3Semibold});
      }
      &[data-size='small'] {
        font: var(--zds-body-3, ${TypographyCSSFont.paragraph3Semibold});
      }
      &[data-size='medium'] {
        font: var(--zds-body-3, ${TypographyCSSFont.paragraph3Semibold});
      }
      &[data-size='large'] {
        font: var(--zds-paragraph-header-3, ${TypographyCSSFont.paragraph1ShortSemibold});
      }
    }

    &[data-color='danger'] {
      ${Vars.baseBackgroundColor}: var(--zds-status-error-stronger, ${Colors.error400});
      ${Vars.baseColor}: var(--zds-background-weaker, ${Colors.neutral100});
      ${Vars.hoverBackgroundColor}: var(--zds-status-error-strongest, ${Colors.error500});
      ${Vars.hoverColor}: var(--zds-background-weaker, ${Colors.neutral100});
      ${Vars.focusColor}: var(--zds-background-weaker, ${Colors.neutral100});
      ${Vars.focusOutlineColor}: var(--zds-ui-primary-stronger, ${Colors.error400});

      &[data-size='compact'] {
        font: var(--zds-minimal-print-2, ${TypographyCSSFont.paragraph3Semibold});
      }
      &[data-size='xsmall'] {
        font: var(--zds-body-3, ${TypographyCSSFont.paragraph3Semibold});
      }
      &[data-size='small'] {
        font: var(--zds-body-3, ${TypographyCSSFont.paragraph3Semibold});
      }
      &[data-size='medium'] {
        font: var(--zds-body-3, ${TypographyCSSFont.paragraph3Semibold});
      }
      &[data-size='large'] {
        font: var(--zds-paragraph-header-3, ${TypographyCSSFont.paragraph1ShortSemibold});
      }
    }

    &[data-color='secondary'] {
      ${Vars.baseBackgroundColor}: var(--zds-ui-primary-weaker, transparent);
      ${Vars.baseColor}: var(--zds-ui-primary-strongest, ${Colors.blue});
      ${Vars.hoverBackgroundColor}: var(--zds-ui-primary, ${Colors.blue10});
      ${Vars.hoverColor}: var(--zds-background-weaker, ${Colors.blue});
      ${Vars.focusBackgroundColor}: var(--zds-ui-primary-weaker, ${Colors.blue10});
      ${Vars.focusColor}: var(--zds-ui-primary-strongest, ${Colors.blue});
      ${Vars.baseBorderColor}: var(--zds-ui-primary-weaker, ${Colors.blue});
      ${Vars.focusOutlineColor}: var(--zds-ui-primary-stronger, ${Colors.blue});

      &[data-size='compact'] {
        font: var(--zds-minimal-print-2, ${TypographyCSSFont.paragraph3Semibold});
      }
      &[data-size='xsmall'] {
        font: var(--zds-body-2, ${TypographyCSSFont.paragraph3Semibold});
      }
      &[data-size='small'] {
        font: var(--zds-body-2, ${TypographyCSSFont.paragraph3Semibold});
      }
      &[data-size='medium'] {
        font: var(--zds-body-2, ${TypographyCSSFont.paragraph3Semibold});
      }
      &[data-size='large'] {
        font: var(--zds-paragraph-header-2, ${TypographyCSSFont.paragraph1ShortSemibold});
      }
    }

    &[data-color='tertiary'] {
      ${Vars.baseBorderColor}: var(--zds-stroke-weaker, ${Colors.neutral100});
      ${Vars.baseBackgroundColor}: var(--zds-background-weaker, ${Colors.neutral100});
      ${Vars.baseColor}: var(--zds-text-default, ${Colors.blue});
      ${Vars.hoverBackgroundColor}: var(--zds-background-default, ${Colors.blue10});
      ${Vars.hoverColor}: var(--zds-text-default, ${Colors.blue});
      ${Vars.focusBackgroundColor}: var(--zds-background-weaker, ${Colors.blue10});
      ${Vars.focusColor}: var(--zds-text-default, ${Colors.blue});
      ${Vars.focusOutlineColor}: var(--zds-ui-primary-stronger, ${Colors.blue});

      &[data-size='compact'] {
        font: var(--zds-minimal-print-2, ${TypographyCSSFont.paragraph3Semibold});
      }
      &[data-size='xsmall'] {
        font: var(--zds-body-2, ${TypographyCSSFont.paragraph3Semibold});
      }
      &[data-size='small'] {
        font: var(--zds-body-2, ${TypographyCSSFont.paragraph3Semibold});
      }
      &[data-size='medium'] {
        font: var(--zds-body-2, ${TypographyCSSFont.paragraph3Semibold});
      }
      &[data-size='large'] {
        font: var(--zds-paragraph-header-2, ${TypographyCSSFont.paragraph1ShortSemibold});
      }

      &[disabled] {
        background-color: var(--zds-gray-warm-3, transparent);
      }

      &:focus-visible:not([disabled]),
      a:focus-visible & {
        border-color: var(--zds-stroke-weaker, ${Vars.focusBorderColor});
        outline: none;
      }
    }

    &[data-color='ghost'] {
      ${Vars.baseBorderColor}: transparent;
      ${Vars.baseBackgroundColor}: transparent;
      ${Vars.baseColor}: var(--zds-ui-primary-stronger, #5140BF);
      ${Vars.hoverBackgroundColor}: var(--zds-ui-primary-weakest, ${Colors.blue10});
      ${Vars.hoverColor}: var(--zds-ui-primary-stronger, #5140BF);
      ${Vars.focusBackgroundColor}: var(--zds-background-weaker, ${Colors.blue10});
      ${Vars.focusColor}: var(--zds-ui-primary-stronger, #5140BF);
      ${Vars.focusOutlineColor}: var(--zds-ui-primary-stronger, ${Colors.blue});


      &[data-size='compact'] {
        font: var(--zds-minimal-print-2, ${TypographyCSSFont.paragraph3Semibold});
      }
      &[data-size='xsmall'] {
        font: var(--zds-body-2, ${TypographyCSSFont.paragraph3Semibold});
      }
      &[data-size='small'] {
        font: var(--zds-body-2, ${TypographyCSSFont.paragraph3Semibold});
      }
      &[data-size='medium'] {
        font: var(--zds-body-2, ${TypographyCSSFont.paragraph3Semibold});
      }
      &[data-size='large'] {
        font: var(--zds-paragraph-header-3, ${TypographyCSSFont.paragraph1ShortSemibold});
      }
    }

    &[data-color='icon-alt'] {
      ${Vars.baseBackgroundColor}: var(--zds-ui-primary, ${Colors.neutral100});
      ${Vars.baseBorderColor}: var(--zds-ui-primary, ${Colors.blue});
      ${Vars.baseColor}: var(--zds-background-weaker, ${Colors.blue});
      ${Vars.hoverColor}: var(--zds-background-weaker, ${Colors.blue});
      ${Vars.hoverBackgroundColor}: var(--zds-ui-primary-stronger, ${Colors.blue10});
      ${Vars.focusBackgroundColor}: var(--zds-ui-primary, ${Colors.blue10});
      ${Vars.focusBorderColor}: var(--ui-primary, ${Colors.blue});
      ${Vars.focusColor}: var(--zds-background-weaker, ${Colors.blue});
      ${Vars.focusOutlineColor}: var(--zds-ui-primary-stronger, ${Colors.blue});

      &[disabled] {
        background-color: var(--zds-background-strongest, ${Vars.disabledBackgroundColor});
        border-color: var(--zds-background-strongest, ${Colors.neutral400});
        color: var(--zds-text-weakest, ${Colors.neutral500});
        outline-color: var(${Vars.disabledOutlineColor});
      }
    }

    &[data-color='icon-brand'] {
      ${Vars.baseBackgroundColor}: var(--zds-ui-brand, #FF4F00);
      ${Vars.baseColor}: var(--zds-text-inverted, #FFFDF9);
      ${Vars.hoverBackgroundColor}: var(--zds-ui-brand-stronger, #CC3F00);
      ${Vars.focusOutlineColor}: var(--zds-ui-primary-stronger, #695BE8);

      &[disabled] {
        background-color: var(--zds-background-stronger, #F5F3EB);
        border-color: var(--zds-background-stronger, #F5F3EB);
        color: var(--zds-text-weaker, #574E4C);
        outline-color: var(${Vars.disabledOutlineColor});
      }
    }

    &[data-color='icon-ghost'] {
      ${Vars.baseColor}: var(--zds-gray-warm-8, #5140BF);
      ${Vars.hoverBackgroundColor}: var(--zds-ui-primary-weakest, ${Colors.blue10});
      ${Vars.hoverColor}: var(--zds-gray-warm-8, #5140BF);
      ${Vars.focusBackgroundColor}: var(--zds-background-weaker, ${Colors.blue10});
      ${Vars.focusColor}: var(--zds-gray-warm-8, #5140BF);
      ${Vars.focusOutlineColor}: var(--zds-ui-primary-stronger, ${Colors.blue});

      &[disabled] {
        background-color: var(--zds-background-stronger, #F5F3EB);
        border-color: var(--zds-background-stronger, #F5F3EB);
        color: var(--zds-text-weaker, #574E4C);
        outline-color: var(${Vars.disabledOutlineColor});
      }
    }

    &[data-color='icon-primary'] {
      ${Vars.baseBackgroundColor}: var(--zds-ui-primary-stronger, ${Colors.neutral100});
      ${Vars.baseBorderColor}: var(--zds-ui-primary-stronger, ${Colors.neutral600});
      ${Vars.baseColor}: var(--zds-background-weaker, ${Colors.neutral800});
      ${Vars.hoverColor}: var(--zds-background-weaker, ${Colors.blueJeans});
      ${Vars.hoverBackgroundColor}: var(--zds-ui-primary-strongest, ${Colors.neutral100});
      ${Vars.focusBorderColor}: var(--zds-ui-primary-stronger, ${Colors.blueJeans});
      ${Vars.focusColor}: var(--zds-background-weaker, ${Colors.blueJeans});
      ${Vars.focusOutlineColor}: var(--zds-ui-primary-stronger, ${Colors.blueJeans});

      font: var(--zds-body-2, ${TypographyCSSFont.paragraph3Semibold});

      &[disabled] {
        background-color: var(--zds-background-strongest, ${Vars.disabledBackgroundColor});
        border-color: var(--zds-background-strongest, ${Colors.neutral400});
        color: var(--zds-text-weakest, ${Colors.neutral500});
        outline-color: var(${Vars.disabledOutlineColor});
      }
    }

    &[data-color='icon-secondary'] {
      ${Vars.baseBackgroundColor}: var(--zds-ui-primary-weaker, ${Colors.neutral100});
      ${Vars.baseBorderColor}: var(--zds-ui-primary-weaker, ${Colors.neutral600});
      ${Vars.baseColor}: var(--zds-ui-primary-stronger, ${Colors.neutral600});
      ${Vars.hoverBackgroundColor}: var(--zds-ui-primary, ${Colors.neutral100});
      ${Vars.hoverColor}: var(--zds-ui-primary-strongest, ${Colors.neutral700});
      ${Vars.focusBorderColor}: var(--zds-ui-primary-weaker, ${Colors.blueJeans});
      ${Vars.focusColor}: var(--zds-ui-primary-stronger, ${Colors.blueJeans});
      ${Vars.focusOutlineColor}: var(--zds-ui-primary-stronger, ${Colors.blueJeans});

      font: var(--zds-body-2, ${TypographyCSSFont.paragraph3Semibold});

      &[disabled] {
        background-color: var(--zds-background-strongest, ${Vars.disabledBackgroundColor});
        border-color: var(--zds-background-strongest, ${Colors.neutral400});
        color: var(--zds-text-weakest, ${Colors.neutral500});
        outline-color: var(${Vars.disabledOutlineColor});
      }
    }

    &[data-color='icon-tertiary'] {
      ${Vars.baseBackgroundColor}: var(--zds-background-weaker, transparent);
      ${Vars.baseColor}: var(--zds-text-weaker, ${Colors.blue});
      ${Vars.baseBorderColor}: var(--zds-stroke-weaker, transparent);
      ${Vars.hoverBackgroundColor}: var(--zds-background-default, ${Colors.blue10});
      ${Vars.hoverColor}: var(--zds-text-weaker, ${Colors.blue});
      ${Vars.focusBackgroundColor}: var(--zds-background-weaker, ${Colors.blue10});
      ${Vars.focusColor}: var(--zds-text-weaker, ${Colors.blue});
      ${Vars.focusOutlineColor}: var(--zds-ui-primary-stronger, ${Colors.blue});

      font: var(--zds-body-2, ${TypographyCSSFont.paragraph3Semibold});

      &[disabled] {
        background-color: var(--zds-background-strongest, transparent);
        border: var(--zds-background-strongest, none);
        color: var(--zds-text-weakest, ${Colors.neutral500});
      }
    }

    &[data-color='number'] {
      ${Vars.baseBackgroundColor}: ${ColorsCSS.neutral100};
      ${Vars.baseBorderColor}: ${ColorsCSS.neutral600};
      ${Vars.baseColor}: ${ColorsCSS.neutral700};
      ${Vars.hoverBackgroundColor}: ${ColorsCSS.blueJeans};
      ${Vars.hoverBorderColor}: ${ColorsCSS.blueJeans};
      ${Vars.hoverColor}: ${ColorsCSS.neutral100};
      ${Vars.focusBorderColor}: ${ColorsCSS.blueJeans};
      ${Vars.focusColor}: ${ColorsCSS.blueJeans};
      ${Vars.focusOutlineColor}: ${ColorsCSS.blueJeans};

      font: var(--zds-body-2, ${TypographyCSSFont.paragraph3Semibold}) !important;

      &[disabled] {
        background-color: var(${Vars.disabledBackgroundColor});
        border-color: ${ColorsCSS.neutral400};
        color: ${ColorsCSS.neutral500};
        outline-color: var(${Vars.disabledOutlineColor});
      }
    }

    &[data-color='primary-dark'],
    &[data-color='icon-primary-dark'] {
      ${Vars.baseBackgroundColor}: var(--zds-brand-almost-white, ${Colors.neutral100});
      ${Vars.baseColor}: var(--zds-ui-primary-stronger, ${Colors.blue});
      ${Vars.hoverColor}: var(--zds-ui-primary-strongest, ${Colors.night});
      ${Vars.hoverBackgroundColor}: var(--zds-ui-primary-weakest, ${Colors.neutral100});
      ${Vars.focusColor}: var(--zds-ui-primary-strongest, ${Colors.blue});
      ${Vars.focusOutlineColor}: var(--zds-background-weaker, ${Colors.neutral100});

      &[data-size='compact'] {
        font: var(--zds-minimal-print-3, ${TypographyCSSFont.paragraph3Semibold});
      }
      &[data-size='xsmall'] {
        font: var(--zds-body-3, ${TypographyCSSFont.paragraph3Semibold});
      }
      &[data-size='small'] {
        font: var(--zds-body-3, ${TypographyCSSFont.paragraph3Semibold});
      }
      &[data-size='medium'] {
        font: var(--zds-body-3, ${TypographyCSSFont.paragraph3Semibold});
      }
      &[data-size='large'] {
        font: var(--zds-paragraph-header-3, ${TypographyCSSFont.paragraph1ShortSemibold});
      }
    }

    &[data-color='secondary-dark'],
    &[data-color='icon-secondary-dark'] {
      ${Vars.baseBackgroundColor}: transparent;
      ${Vars.baseColor}: ${Colors.GrayWarm1};
      ${Vars.baseBorderColor}: ${Colors.GrayWarm1};
      ${Vars.hoverColor}: ${Colors.GrayWarm10};
      ${Vars.hoverBackgroundColor}: ${Colors.UiPrimaryWeakest};
      ${Vars.hoverBorderColor}: ${Colors.UiPrimaryWeakest};
      ${Vars.focusColor}: ${Colors.GrayWarm10};
      ${Vars.focusOutlineColor}: ${Colors.UiPrimaryWeakest};
      ${Vars.focusBackgroundColor}: ${Colors.UiPrimaryWeakest};

      &[data-size='compact'] {
        font: var(--zds-minimal-print-2, ${TypographyCSSFont.paragraph3Semibold});
      }
      &[data-size='xsmall'] {
        font: var(--zds-body-3, ${TypographyCSSFont.paragraph3Semibold});
      }
      &[data-size='small'] {
        font: var(--zds-body-3, ${TypographyCSSFont.paragraph3Semibold});
      }
      &[data-size='medium'] {
        font: var(--zds-body-3, ${TypographyCSSFont.paragraph3Semibold});
      }
      &[data-size='large'] {
        font: var(--zds-paragraph-header-3, ${TypographyCSSFont.paragraph1ShortSemibold});
      }
    }

    &[data-color='button-nav'] {
      ${Vars.baseBorderColor}: var(--zds-stroke-weaker, ${Colors.neutral600});
      ${Vars.baseBackgroundColor}: var(--zds-background-weaker, ${Colors.neutral200});
      ${Vars.baseColor}: var(--zds-text-default, ${Colors.neutral800});
      ${Vars.hoverBackgroundColor}: var(--zds-background-default, ${Colors.blue});
      ${Vars.hoverColor}: var(--zds-text-default, ${Colors.neutral100});
      ${Vars.focusBackgroundColor}: var(--zds-background-weaker, ${Colors.neutral200});
      ${Vars.focusColor}: var(--zds-text-default, ${Colors.blue});
      ${Vars.focusOutlineColor}: var(--zds-ui-primary-stronger, ${Colors.blue});
      ${Vars.focusBorderColor}: ${Colors.neutral200};

      font: var(--zds-body-2, ${TypographyCSSFont.paragraph3Semibold});
      border-radius: 30px;
      &::before {
        border-radius: 30px;
      }
      &[data-selected] {
        ${Vars.baseBorderColor}: var(--zds-gray-warm-9, ${Colors.neutral700});
        ${Vars.baseBackgroundColor}: var(--zds-gray-warm-9, ${Colors.neutral700});
        ${Vars.baseColor}: var(--zds-text-inverted, ${Colors.neutral100});
        ${Vars.hoverBackgroundColor}: var(--zds-gray-warm-8, ${Colors.neutral700});
        ${Vars.hoverColor}: var(--zds-text-inverted, ${Colors.neutral100});
        ${Vars.focusBackgroundColor}: var(--zds-gray-warm-8, ${Colors.neutral700});
        ${Vars.focusColor}: var(--zds-text-inverted, ${Colors.neutral100});
        ${Vars.focusOutlineColor}: var(--zds-ui-gray-warm-8, ${Colors.neutral700});
      }
    }

    &[data-color='brand'],
    &[data-color='brand-nav'] {
      ${Vars.baseBackgroundColor}: var(--zds-ui-brand, ${Colors.zapier});
      ${Vars.baseColor}: var(--zds-text-inverted, #FFFDF9);
      ${Vars.baseBorderColor}: var(--zds-ui-brand, transparent);
      ${Vars.hoverColor}: var(--zds-background-weaker, #FFFDF9);
      ${Vars.hoverBackgroundColor}: var(--zds-ui-brand-stronger, #CC3F00);
      ${Vars.focusColor}: var(--zds-text-inverted, #FFFDF9);
      ${Vars.focusOutlineColor}: var(--zds-ui-primary, ${Colors.zapier});
      ${Vars.focusBackgroundColor}: var(--zds-ui-brand, ${Colors.zapier});
      ${Vars.focusBorderColor}: var(--zds-ui-brand, transparent);

      &:hover:not([disabled]),
      a:hover & {
        border-color: var(--zds-ui-brand-stronger, #CC3F00});
      }
    }

    &[data-color='brand'] {
      &[data-size='compact'] {
        font: var(--zds-minimal-print-3, ${TypographyCSSFont.paragraph3Semibold});
      }
      &[data-size='small'] {
        font: var(--zds-body-3, ${TypographyCSSFont.paragraph1ShortBold});
        border-radius: var(--zds-radius-large, 18px);
        &::before {
          border-radius: var(--zds-radius-small, 20px);
        }
      }
      &[data-size='medium'] {
        font: var(--zds-body-3, ${TypographyCSSFont.paragraph1ShortBold});
        border-radius: var(--zds-radius-large, 18px);
        &::before {
          border-radius: var(--zds-radius-small, 20px);
        }
      }
      &[data-size='large'] {
        font: var(--zds-paragraph-header-3, ${TypographyCSSFont.paragraph1ShortBold});
        border-radius: var(--zds-radius-large, 24px);
        &::before {
          border-radius: var(--zds-radius-small, 26px);
        }
      }
    }

    &[data-color='brand-nav'] {
      ${Vars.iconMargin}: 0;
      &[data-size='medium'] {
        var(--zds-body-3, ${TypographyCSSFont.paragraph1ShortBold});
        border-radius: var(--zds-radius-large, 18px);
        &::before {
          border-radius: var(--zds-radius-small, 22px);
        }
      }
    }

    &[disabled] {
      cursor: not-allowed;
      background-color: var(${Vars.disabledBackgroundColor});
      border-color: var(${Vars.disabledBorderColor});
      color: var(${Vars.disabledColor});
      outline-color: var(${Vars.disabledOutlineColor});
    }

    &[data-full-width] {
      width: 100%;
    }

    &[data-shadowed] {
      box-shadow: ${Shadows.elevation5};
    }

    &[data-size='compact'] {
      ${Vars.buttonContentPadding}: 0 var(--zds-space-12, 15px);
      height: var(--zds-size-small, 30px);

      &[data-icon] {
        /* Last value accounts for empty space inside svg */
        ${Vars.buttonContentPadding}: 0 var(--zds-space-12, 15px) 0 var(--zds-space-8, 13px);
      }

      &[data-square] {
        width: var(--zds-size-small, 30px);
      }
    }

    &[data-size='xsmall'] {
      ${Vars.buttonContentPadding}: 0 var(--zds-space-12, 14px);

      height: var(--zds-size-small, 30px);
      border-radius: 3px;

      &[data-icon] {
        ${Vars.buttonContentPadding}: 0 var(--zds-space-12, 6px) 0 var(--zds-space-8, 4px);
      }

      &[data-square] {
        width: var(--zds-size-small, 30px);
      }
    }

    &[data-size='small'] {
      ${Vars.buttonContentPadding}: 0 var(--zds-space-12, 20px);
      height: var(--zds-size-small, 44px);

      &[data-icon] {
        ${Vars.buttonContentPadding}: 0 var(--zds-space-12, 20px) 0 var(--zds-space-8, 18px);
      }

      &[data-square] {
        width: var(--zds-size-small, 44px);
        height: var(--zds-size-small, 44px);
      }
      &[data-color='button-nav'] {
        ${Vars.buttonContentPadding}: 0 var(--zds-space-12, 20px);
      }
    }

    &[data-size='medium'] {
      ${Vars.buttonContentPadding}: 0 var(--zds-space-12, 20px);
      height: var(--zds-size-medium, 50px);

      &[data-icon] {
        ${Vars.buttonContentPadding}: 0 var(--zds-space-12, 20px) 0 var(--zds-space-8, 18px);
        &[data-color='brand-nav'] {
          ${Vars.buttonContentPadding}: 0 12px;
        }
      }

      &[data-square] {
        width: var(--zds-size-medium, 50px);
      }
    }

    &[data-size='large'] {
      ${Vars.buttonContentPadding}: 0 var(--zds-space-12, 20px);
      height: var(--zds-size-large, 60px);

      &[data-icon] {
        ${Vars.buttonContentPadding}: 0 var(--zds-space-12, 20px) 0 var(--zds-space-8, 18px);
      }

      &[data-square] {
        width: var(--zds-size-large, 60px);
      }
    }

    &[data-unpadded],
    &[data-square] {
      ${Vars.buttonContentPadding}: 0 !important;
    }

    &[data-pill] {
      border-radius: var(--zds-radius-pill, 100px) !important;

      &::before {
        border-radius: var(--zds-radius-pill, 100px) !important;
      }
    }

    /*
    IconButton size styles
    */
      &[data-size='compact'],&[data-size='xsmall'],&[data-size='small'] {
        [data-testid='iconContainer'] {
          width: var(--zds-size-20, ${iconSize}px);
          height: var(--zds-size-20, ${iconSize}px);
        }
      }
      &[data-size='medium'] {
        [data-testid='iconContainer'] {
          width: var(--zds-size-24, ${iconSize}px);
          height: var(--zds-size-24, ${iconSize}px);
        }
      }
      &[data-size='large'] {
        [data-testid='iconContainer'] {
          width: var(--zds-size-32, ${iconSize}px);
          height: var(--zds-size-32, ${iconSize}px);
        }
      }
    */
  `,
  buttonContent: css`
    display: flex;
    align-items: center;
    min-width: 0;
    padding: var(${Vars.buttonContentPadding});
  `,
  buttonText: css`
    display: inline-block;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
  `,
  buttonIcon: (hasSibling: boolean) => css`
    display: block;
    flex: none;
    ${hasSibling && `margin-right: var(${Vars.iconMargin});`}
  `,
});
